import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../auth';
import { authHeaders, jsonAuthHeaders } from '../../../shared/authHeaders';
import { formatMoney } from '../../../shared/money';

interface RefundRequest {
  id: string;
  roomCode: string | null;
  roomName: string | null;
  requesterName: string | null;
  buyerEmail: string | null;
  amountCents: number | null;
  currency: string | null;
  status: 'pending' | 'handled';
  requestedAt: string;
  resolutionNote: string | null;
}

function formatDate(value: string) {
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function RefundRequestsPage() {
  const { getToken } = useAuth();
  const [status, setStatus] = useState<'pending' | 'handled'>('pending');
  const [requests, setRequests] = useState<RefundRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [resolveTarget, setResolveTarget] = useState<RefundRequest | null>(null);
  const [resolutionNote, setResolutionNote] = useState('');
  // "Notify user by email" defaults ON for refund resolutions (phase5 area 7).
  const [notifyUser, setNotifyUser] = useState(true);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const response = await fetch(`/api/admin/refund-requests?status=${status}`, {
        headers: authHeaders(token),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load refund requests');
      }
      setRequests(data.refundRequests || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load refund requests');
    } finally {
      setLoading(false);
    }
  }, [getToken, status]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const openResolveDialog = (request: RefundRequest) => {
    setResolveTarget(request);
    setResolutionNote('');
    setNotifyUser(true);
  };

  const markHandled = async () => {
    if (!resolveTarget || !resolutionNote.trim()) return;

    setResolvingId(resolveTarget.id);
    setError(null);
    try {
      const token = await getToken();
      const response = await fetch(`/api/admin/refund-requests/${resolveTarget.id}`, {
        method: 'PATCH',
        headers: jsonAuthHeaders(token),
        body: JSON.stringify({ resolutionNote: resolutionNote.trim(), notifyUser }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to resolve refund request');
      }
      setResolveTarget(null);
      await loadRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resolve refund request');
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-text">Refund Requests</h1>
          <p className="mt-1 text-sm text-text-dim">
            Manual review queue for cancelled paid tickets. Marking handled does not issue a refund; process any Stripe refund first, then record it here.
          </p>
        </div>
        <div role="tablist" aria-label="Refund request status" className="inline-flex w-fit rounded-lg border border-line bg-panel p-1">
          {(['pending', 'handled'] as const).map((item) => (
            <button
              key={item}
              type="button"
              role="tab"
              aria-selected={status === item}
              onClick={() => setStatus(item)}
              className={`rounded-md px-3 py-1.5 text-sm capitalize transition-colors ${
                status === item ? 'bg-accent text-white' : 'text-text-dim hover:text-text'
              }`}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div role="alert" aria-live="polite" className="mb-4 rounded-lg border border-warn/30 bg-warn/10 px-4 py-3 text-sm text-warn">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-line bg-panel">
        {loading ? (
          <div className="p-6 text-sm text-text-dim">Loading refund requests...</div>
        ) : requests.length === 0 ? (
          <div className="p-6 text-sm text-text-dim">No {status} refund requests.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-line bg-panel2 text-xs uppercase text-text-faint">
                <tr>
                  <th className="px-4 py-3 font-medium">Event</th>
                  <th className="px-4 py-3 font-medium">Buyer</th>
                  <th className="px-4 py-3 font-medium">Amount</th>
                  <th className="px-4 py-3 font-medium">Requested</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {requests.map((request) => (
                  <tr key={request.id}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-text">{request.roomName || 'Unknown event'}</div>
                      {request.roomCode && <div className="text-xs text-text-faint">/{request.roomCode}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-text">{request.requesterName || 'Unknown buyer'}</div>
                      {request.buyerEmail && <div className="text-xs text-text-faint">{request.buyerEmail}</div>}
                    </td>
                    <td className="px-4 py-3 text-text">
                      {request.amountCents === null ? 'Unknown' : formatMoney(request.amountCents, request.currency)}
                    </td>
                    <td className="px-4 py-3 text-text-dim">{formatDate(request.requestedAt)}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full border px-2 py-1 text-xs capitalize ${
                        request.status === 'pending'
                          ? 'border-warn/20 bg-warn/10 text-warn'
                          : 'border-ok/20 bg-ok/10 text-ok'
                      }`}>
                        {request.status}
                      </span>
                      {request.resolutionNote && (
                        <div title={request.resolutionNote} className="mt-1 max-w-[240px] truncate text-xs text-text-faint">{request.resolutionNote}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {request.status === 'pending' && (
                        <button
                          type="button"
                          onClick={() => openResolveDialog(request)}
                          disabled={resolvingId === request.id}
                          className="btn btn-sm disabled:opacity-50"
                        >
                          {resolvingId === request.id ? 'Saving...' : 'Mark handled'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {resolveTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => resolvingId === null && setResolveTarget(null)} />
          <div className="relative w-full max-w-md rounded-xl border border-line bg-panel p-5 space-y-4">
            <h2 className="text-lg font-medium text-text">Mark refund request handled</h2>
            <p className="text-sm text-text-dim">
              {resolveTarget.amountCents === null ? 'Unknown' : formatMoney(resolveTarget.amountCents, resolveTarget.currency)} for{' '}
              {resolveTarget.requesterName || resolveTarget.buyerEmail || 'this buyer'}. Only confirm after any Stripe
              or manual refund work is complete — this does not issue money.
            </p>
            <label className="block">
              <span className="block text-[10px] font-mono text-text-faint uppercase mb-1">Handling note (required)</span>
              <textarea
                data-testid="refund-resolve-note"
                value={resolutionNote}
                onChange={(e) => setResolutionNote(e.target.value)}
                placeholder="How was this handled? Include the Stripe refund ID or manual operations note."
                className="input w-full h-20 resize-none"
              />
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="refund-resolve-notify"
                checked={notifyUser}
                onChange={(e) => setNotifyUser(e.target.checked)}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm text-text">Notify user by email</span>
            </label>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setResolveTarget(null)}
                disabled={resolvingId !== null}
                className="btn"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="refund-resolve-confirm"
                onClick={markHandled}
                disabled={resolvingId !== null || !resolutionNote.trim()}
                className="btn btn-accent disabled:opacity-50"
              >
                {resolvingId !== null ? 'Saving...' : 'Mark handled'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
